/**
 * How the dashboard writes an amount of work. The API counts work in hours
 * (an item's shown estimate, its actual hours, a month's budget); people read
 * small amounts in hours and larger ones in working days of 8 hours, the unit
 * the monthly budget is agreed in.
 *
 * - `hoursLabel`: always in hours, "4 u", "4 h".
 * - `daysLabel`: always in days, "0,75 dag", "3 dagen", "1.5 days", for a
 *   month's totals.
 * - `effortLabel`: an item's effort, in hours below 8 and in days from 8 up,
 *   "Nog te schatten" while it has no estimate.
 * - `EFFORT_STEPS`: the efforts an item can have, the steps a developer
 *   chooses from.
 * - `effortStepTargets` and `effortHoursAndDays`: the effort stepper's moves
 *   and its value, "12 u (1,5 dag)".
 *
 * Numbers follow the language ("1,5" in Dutch and French, "1.5" in English),
 * with up to two decimals. The unit's singular or plural follows each
 * language's own rule (`dayForm`), which i18next's plural keys cannot carry:
 * Dutch writes "1,5 dag" but "2 dagen" and "2,5 dagen", French "1,5 jour"
 * but "2 jours", English "1 day" but "1.5 days".
 */

import { formatNumber } from "@/lib/format";
import { i18n } from "@/lib/i18n";
import { getLanguage, type Language } from "@/lib/i18n/language";

/** A working day, the unit the monthly budget is agreed in. */
export const HOURS_PER_DAY = 8;

/** Rounded to two decimals, as the labels show it: the unit agrees with what is shown. */
function rounded(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Whether a number of days takes the singular: in Dutch anything above 0 and
 * below two ("0,75 dag", "1 dag", "1,5 dag", but "0 dagen", "2 dagen",
 * "2,5 dagen"), in French anything below two ("0 jour", "1,5 jour",
 * "2 jours"), in English exactly one ("1 day", "0.75 days").
 */
function dayForm(days: number, language: Language): "day" | "days" {
  const size = Math.abs(days);
  switch (language) {
    case "nl":
      return size > 0 && size < 2 ? "day" : "days";
    case "fr":
      return size < 2 ? "day" : "days";
    case "en":
      return size === 1 ? "day" : "days";
  }
}

/** An amount of hours in hours, in the dashboard's language: "4 u", "4 h". */
export function hoursLabel(hours: number, language: Language = getLanguage()): string {
  return i18n.t("roadmap.effort.hours", {
    hours: formatNumber(rounded(hours), {}, language),
    lng: language,
  });
}

/**
 * An amount of hours in working days of 8 hours, in the dashboard's
 * language: "0 dagen", "0,75 dag", "3 dagen"; "1.5 days"; "2 jours".
 */
export function daysLabel(hours: number, language: Language = getLanguage()): string {
  const days = rounded(hours / HOURS_PER_DAY);
  return i18n.t(`roadmap.effort.${dayForm(days, language)}`, {
    days: formatNumber(days, {}, language),
    lng: language,
  });
}

/**
 * An item's effort in the dashboard's language: in hours below a day ("2 u",
 * "4 u"), in days from a day up ("1 dag", "1,5 dag", "2 dagen"), and "Nog te
 * schatten" while there is no estimate yet.
 */
export function effortLabel(
  hours: number | null | undefined,
  language: Language = getLanguage(),
): string {
  if (hours === null || hours === undefined) {
    return i18n.t("roadmap.effort.unknown", { lng: language });
  }
  return hours < HOURS_PER_DAY ? hoursLabel(hours, language) : daysLabel(hours, language);
}

/**
 * The efforts a developer chooses from for an item, in hours: 2 and 4 hours,
 * then from a day up in half days to 5 days ("2 u", "4 u", "1 dag",
 * "1,5 dag", … "5 dagen"). The server stores an item's effort as one of
 * these steps, never a raw estimate.
 */
export const EFFORT_STEPS: readonly number[] = [2, 4, 8, 12, 16, 20, 24, 28, 32, 36, 40];

/**
 * The steps to offer for an item whose effort is `current`: `EFFORT_STEPS`,
 * and `current` in its place when it lies beyond them (6 days, set earlier),
 * so the choice always shows what the item holds.
 */
export function effortOptions(current: number | null | undefined): number[] {
  const steps = [...EFFORT_STEPS];
  if (current !== null && current !== undefined && !steps.includes(current)) {
    steps.push(current);
    steps.sort((a, b) => a - b);
  }
  return steps;
}

/**
 * Where an effort stepper's "−" and "+" go from `current`: the step below and
 * the step above (`effortOptions`), in hours. Below the first step lies "Nog
 * te schatten" (null) when `allowNone` (an idea), and from there "+" goes to
 * the first step. Undefined where the stepper ends: under 2 hours for an item
 * that keeps an effort, and above the last step.
 */
export function effortStepTargets(
  current: number | null,
  allowNone: boolean,
): { down: number | null | undefined; up: number | null | undefined } {
  const steps = effortOptions(current);
  if (current === null) {
    return { down: undefined, up: steps[0] };
  }
  const index = steps.indexOf(current);
  return {
    down: index > 0 ? steps[index - 1] : allowNone ? null : undefined,
    up: steps[index + 1],
  };
}

/**
 * An effort in hours and in days together, as the effort stepper shows it:
 * "12 u (1,5 dag)", "4 u (0,5 dag)", "12 h (1.5 days)"; "Nog te schatten"
 * while there is no estimate.
 */
export function effortHoursAndDays(
  hours: number | null,
  language: Language = getLanguage(),
): string {
  if (hours === null) {
    return i18n.t("roadmap.effort.unknown", { lng: language });
  }
  return i18n.t("roadmap.effort.hoursAndDays", {
    hours: hoursLabel(hours, language),
    days: daysLabel(hours, language),
    lng: language,
  });
}
